import React, { useCallback, useEffect, useRef, useState } from "react";
import { PathTypeSchema } from "@domain/schemas/BI";
import { SingleFileOverwriteConfirmDialog } from "./SingleFileOverwriteConfirmDialog";
import { useUploadDestinationPicker } from "./useUploadDestinationPicker";
import type { UploadDestination } from "./UploadDestinationPickerDialog";

interface PrepareUploadResult {
  proceed: boolean;
  rootPath?: string;
  folder?: string;
}

interface OverwriteState {
  fileName: string;
  existingSize: number;
  resolve: (isConfirmed: boolean) => void;
}

/**
A project can have more than one configured path for a given facet (Fasta/SoftwareSettings) - when
that happens an upload destination is ambiguous and must never be silently defaulted (would make the
file invisible to whichever context expected it under its own override). This hook resolves the
destination-picker (root + subfolder, via `useUploadDestinationPicker`) and overwrite-confirmation
steps that must both complete (if applicable) before an upload is allowed to proceed, returning the
chosen `rootPath`/`folder` to send with the actual upload call. Render the returned `dialogs` element
alongside the component's other dialogs.
*/
export function useUploadDestinationFlow(
  pathType: PathTypeSchema,
  projectId: string | null | undefined,
  checkFileExistsAsync: (fileName: string, rootPath: string | undefined, folder: string | undefined) => Promise<number | null>,
  initialDestination?: Partial<UploadDestination>,
) {
  const { dialog: destinationDialog, ...picker } = useUploadDestinationPicker(pathType, projectId, initialDestination);
  const { pickDestination } = picker;
  const [overwriteState, setOverwriteState] = useState<OverwriteState | null>(null);
  const pendingOverwrite = useRef<((isConfirmed: boolean) => void) | null>(null);
  const generation = useRef(0);
  useEffect(
    () => () => {
      generation.current++;
      pendingOverwrite.current?.(false);
      pendingOverwrite.current = null;
    },
    [projectId, pathType],
  );

  const prepareUpload = useCallback(
    async (fileName: string, selectedDestination?: UploadDestination): Promise<PrepareUploadResult> => {
      if (!projectId) {
        return { proceed: false };
      }
      const currentGeneration = generation.current;

      const destination = selectedDestination ?? (await pickDestination(fileName));
      if (!destination) {
        return { proceed: false };
      }
      const { rootPath, folder } = destination;

      const existingSize = await checkFileExistsAsync(fileName, rootPath, folder);
      if (currentGeneration !== generation.current) return { proceed: false };
      if (existingSize !== null && existingSize !== undefined) {
        const isConfirmed: boolean = await new Promise<boolean>((resolve) => {
          pendingOverwrite.current = resolve;
          setOverwriteState({ fileName, existingSize, resolve });
        });
        pendingOverwrite.current = null;
        setOverwriteState(null);
        if (!isConfirmed || currentGeneration !== generation.current) {
          return { proceed: false };
        }
      }

      return { proceed: true, rootPath, folder };
    },
    [projectId, pickDestination, checkFileExistsAsync],
  );

  const dialogs = (
    <>
      {destinationDialog}
      {overwriteState && (
        <SingleFileOverwriteConfirmDialog
          fileName={overwriteState.fileName}
          existingSize={overwriteState.existingSize}
          onConfirm={() => overwriteState.resolve(true)}
          onCancel={() => overwriteState.resolve(false)}
        />
      )}
    </>
  );

  return { dialogs, prepareUpload, ...picker };
}
